import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Device } from "@twilio/voice-sdk";
import { setVoiceCallInfo } from "../../redux/actions/communicationVoiceCallInfo";
import styles from "./communication-voice.module.css";
import { Phone, ArrowLeft, X } from "react-feather";
import Loader from "../Loader";
import { toast } from "react-smart-toaster";
import { AddLogCallAPI } from "../AddLogs";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";

const strGenerate = () => {
  const characters =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  const charactersLength = characters.length;
  for (let i = 0; i < 10; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

const CommunicationVoiceCall = () => {
  const redirect = useHistory();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const voiceCallInfo = useSelector(
    (store) => store.communicationVoiceCallInfoReducer
  );
  const [callStatus, setCallStatus] = useState("");
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(false);

  // Notes modal flow state
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [callDuration, setCallDuration] = useState(0); // in seconds
  const [callNotesText, setCallNotesText] = useState("");

  // Snapshot of call info captured at disconnect time (before voiceCallInfo
  // gets nulled out by disConnectCall). This is what addCallNote should use,
  // NOT the live voiceCallInfo from Redux, since that may already be null
  // by the time the user clicks Skip/Save.
  const [activeCallInfo, setActiveCallInfo] = useState(null);

  useEffect(() => {
    console.log("voice component", voiceCallInfo);

    const connectCall = async () => {
      console.log("Connect Call");
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      });

      setLoading(true);

      axios
        .get(
          process.env.REACT_APP_COMMUNICATION_NODE_API_URL +
            "voice/get-call-token/" +
            voiceCallInfo?.id +
            "-" +
            strGenerate(),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          let twilioToken = response.data.data.token;

          let isCallStarted = 0;
          let callStartTime = null;
          const newDevice = new Device(twilioToken);

          setDevice(newDevice);

          let pPhone = voiceCallInfo?.phone;
          pPhone = pPhone.replace(/\D/g, "");
          pPhone = "+1" + pPhone;
          console.log("pPhone", pPhone);
          newDevice
            .connect({
              params: {
                To: pPhone,
                From: process.env.REACT_APP_TWILIO_VOICE_NUMBER,
              },
            })
            .then((callResponse) => {
              setLoading(false);

              callResponse.on("ringing", () => {
                console.log("Ringing...");
                setCallStatus("ringing");
              });

              callResponse.on("accept", () => {
                console.log("Call accepted");
                setCallStatus("accept");

                callStartTime = Date.now();
                isCallStarted = 1;

                let logData = {
                  title: "Voice call initiated",
                  description: [
                    "Name: " +
                      (voiceCallInfo?.full_name !== ""
                        ? voiceCallInfo?.full_name
                        : "-"),
                    "Phone: " + voiceCallInfo?.phone,
                    "Type: " + voiceCallInfo?.type,
                  ],
                };
                AddLogCallAPI(logData, token);
              });

              callResponse.on("disconnect", () => {
                console.log("Call disconnected");
                setCallStatus("disconnect");

                // IMPORTANT: voiceCallInfo here is the value captured by this
                // closure when connectCall() was created — it is still the
                // correct, original call data even if Redux has since been
                // nulled out by disConnectCall(). Snapshot it now into
                // component state so the modal handlers (which read live
                // state on click) have access to it later.
                setActiveCallInfo({
                  id: voiceCallInfo?.id,
                  type: voiceCallInfo?.type,
                  full_name: voiceCallInfo?.full_name,
                  phone: voiceCallInfo?.phone,
                });

                if (
                  voiceCallInfo?.type !== "Manual" &&
                  voiceCallInfo?.type !== "Employee"
                ) {
                  let totalSeconds = 0;
                  if (isCallStarted === 1) {
                    totalSeconds = Math.floor(
                      (Date.now() - callStartTime) / 1000
                    );
                  }
                  setCallDuration(totalSeconds);
                  setOpenNotesModal(true);
                }

                // localStorage.setItem("prospectTabVal", "note");
                // Redirect intentionally removed - user stays on current page.
              });

              callResponse.on("error", (error) => {
                console.error("Connection error:", error);
                setCallStatus("");
                toast.error("Call is not connected. Try again!");
                dispatch(setVoiceCallInfo(null));
              });
            })
            .catch((err2) => {
              setLoading(false);
              console.log(err2);
              toast.error("Call is not connected. Try again!");
              dispatch(setVoiceCallInfo(null));
            });

          newDevice.on("error", (error) => {
            console.error("Device error:", error);
            dispatch(setVoiceCallInfo(null));
          });
        })
        .catch((err) => {
          setLoading(false);
          console.log(err);
          toast.error("Call is not connected. Try again!");
          dispatch(setVoiceCallInfo(null));
        });
    };

    if (voiceCallInfo) {
      connectCall();
    }
  }, [voiceCallInfo]);

  const addCallNote = async (prospectId, totalSeconds, type, customNote) => {
    const token = await getAccessTokenSilently({
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    });

    let noteText =
      "Call Logged: " +
      dayjs().format("MM/DD/YYYY") +
      " at " +
      dayjs().format("hh:mm A") +
      "\nCall log: " +
      formatTime(totalSeconds) +
      " min";

    if (customNote && customNote.trim() !== "") {
      noteText += "\n\nNote: " + customNote.trim();
    }

    if (type === "Prospect") {
      axios
        .post(
          process.env.REACT_APP_APPLICANT_API_URL +
            "v1/applicants/" +
            prospectId +
            "/notes",
          {
            type: "call",
            note: noteText,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then(() => {})
        .catch(() => {});
    }

    if (type === "Tenant") {
      axios
        .post(
          process.env.REACT_APP_TENANT_API_URL +
            "v1/tenants/" +
            prospectId +
            "/notes",
          {
            type: "call",
            note: noteText,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then(() => {})
        .catch(() => {});
    }
  };

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  const disConnectCall = () => {
    device.disconnectAll();
    dispatch(setVoiceCallInfo(null));
  };

  const closeNotesModalAndReset = () => {
    setOpenNotesModal(false);
    setCallNotesText("");
    setCallDuration(0);
    setActiveCallInfo(null);
  };

  const handleSkipNotes = () => {
    addCallNote(activeCallInfo?.id, callDuration, activeCallInfo?.type);
    closeNotesModalAndReset();
  };

  const handleSaveNotes = () => {
    addCallNote(
      activeCallInfo?.id,
      callDuration,
      activeCallInfo?.type,
      callNotesText
    );
    closeNotesModalAndReset();
  };

  return (
    <>
      {loading && <Loader />}
      {(callStatus === "ringing" || callStatus === "accept") && (
        <div className={`${styles.CallBar} CallBar`}>
          <div className={`${styles.CallBarAvatar}`}>
            {voiceCallInfo?.user_avatar !== "" ? (
              <img src={voiceCallInfo?.user_avatar} alt="" />
            ) : voiceCallInfo?.initial !== "?" ? (
              <p>{voiceCallInfo?.initial}</p>
            ) : (
              <img src="/static/img/DefaultLogo.png" alt="" />
            )}
          </div>
          <div className={`${styles.CallBarDetails}`}>
            {voiceCallInfo?.full_name !== "" && (
              <p className={`${styles.Name}`}>{voiceCallInfo?.full_name}</p>
            )}
            <p className={`${styles.Phone}`}>{voiceCallInfo?.phone}</p>
          </div>
          {callStatus === "ringing" && (
            <p className={`${styles.CallRingText}`}>Ringing</p>
          )}
          {callStatus === "accept" && (
            <p className={`${styles.CallCallingText}`}>Calling</p>
          )}
          <button
            className={`${styles.CallDisBU}`}
            onClick={(e) => disConnectCall()}
          >
            <Phone />
          </button>
        </div>
      )}

      <Dialog
        open={openNotesModal}
        fullWidth
        maxWidth="sm"
        onClose={handleSkipNotes}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="NewPopUp MobileForm"
      >
        <DialogTitle id="alert-dialog-title">
          <button className={`${styles.BackAddBU}`} onClick={handleSkipNotes}>
            <ArrowLeft />
          </button>
          Call Completed
          <button onClick={handleSkipNotes}>
            <X />
          </button>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <div className="ModalFormGroup">
              <p className={`${styles.CallDuration}`}>
                <span>Duration:</span> {formatTime(callDuration)} min
              </p>
              <label className="PopupBodyText">
                Would you like to add a note for this call?
              </label>
              <div className={`${styles.NotesField} FormGroup`}>
                <TextField
                  id="outlined-multiline-static"
                  label="Add Notes"
                  multiline
                  rows={6}
                  value={callNotesText}
                  onChange={(e) => setCallNotesText(e.target.value)}
                />
              </div>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSkipNotes} className="CancelPopupBU">
            Skip
          </Button>
          <Button onClick={handleSaveNotes} className="SubmitPopupBU">
            Save Notes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommunicationVoiceCall;
